import { DateFormat } from '@easy-expense/data-schema-v2';

/**
 * Order in which the day and month appear in a numeric statement date.
 * - 'MDY' → month/day/year (US, e.g. 06/13/2025)
 * - 'DMY' → day/month/year (e.g. 13/06/2025)
 */
export type DateOrder = 'MDY' | 'DMY';

/** Matches a numeric date with `/`, `-`, or `.` separators (e.g. 13/06/2025, 2025-06-13). */
const NUMERIC_DATE_RE = /^(\d{1,4})[/\-.](\d{1,2})[/\-.](\d{1,4})$/;
/** Matches an ISO date already in yyyy-MM-dd form. */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Maps the workspace `dateFormat` setting to a {@link DateOrder}. Defaults to
 * month-first (US) when the setting is missing, matching the schema default.
 */
export function dateOrderFromFormat(dateFormat?: DateFormat): DateOrder {
  return dateFormat === 'dd/MM/yy' ? 'DMY' : 'MDY';
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Expands a 2-digit year to 4 digits using the same pivot as the JS engine's
 * legacy `Date` parser (00-49 → 2000s, 50-99 → 1900s), so values like `98`
 * keep mapping to 1998 rather than 2098.
 */
function normalizeYear(year: number): number {
  if (year >= 100) {
    return year;
  }
  return year < 50 ? 2000 + year : 1900 + year;
}

/**
 * Builds a `yyyy-MM-dd` string, validating that the components form a real
 * calendar date within a sensible range. Returns null when they do not.
 */
function buildISODate(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  if (year < 1900 || year > 2100) {
    return null;
  }
  // Reject overflow like Feb 30 (Date would roll it into March).
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${pad(month)}-${pad(day)}`;
}

/**
 * Parses a single trimmed date string to `yyyy-MM-dd`, or null if it cannot be
 * understood. Numeric day/month ordering is resolved by `order`, except when a
 * component is unambiguous (a value > 12 can only be a day), in which case the
 * data wins over the preference.
 */
function parseToISO(trimmed: string, order: DateOrder): string | null {
  if (ISO_DATE_RE.test(trimmed)) {
    const [year, month, day] = trimmed.split('-').map(Number);
    return buildISODate(year!, month!, day!);
  }

  const match = trimmed.match(NUMERIC_DATE_RE);
  if (match) {
    const first = Number(match[1]);
    const second = Number(match[2]);
    const third = Number(match[3]);

    // Year-first format (e.g. 2025/06/13).
    if (match[1]!.length === 4) {
      return buildISODate(first, second, third);
    }

    const year = normalizeYear(third);
    const firstIsDay = first > 12;
    const secondIsDay = second > 12;

    if (firstIsDay && secondIsDay) {
      return null; // Neither component can be a month.
    }
    if (firstIsDay) {
      return buildISODate(year, second, first);
    }
    if (secondIsDay) {
      return buildISODate(year, first, second);
    }
    // Ambiguous → fall back to the preferred order.
    return order === 'DMY' ? buildISODate(year, second, first) : buildISODate(year, first, second);
  }

  // Textual dates (e.g. "Jan 5, 2025") are unambiguous; let the engine parse.
  const parsed = new Date(trimmed);
  if (isNaN(parsed.getTime())) {
    return null;
  }
  const year = parsed.getFullYear();
  if (year < 1900 || year > 2100) {
    return null;
  }
  return `${year}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

/**
 * Inspects a column of raw date values and decides whether they are month-first
 * or day-first. Any unambiguous value (a component > 12) settles the order; when
 * every value is ambiguous or the evidence conflicts, `preferred` is used.
 */
export function detectDateOrder(values: unknown[], preferred: DateOrder): DateOrder {
  let sawDMY = false;
  let sawMDY = false;

  for (const value of values) {
    if (typeof value !== 'string') {
      continue;
    }
    const match = value.trim().match(NUMERIC_DATE_RE);
    if (!match || match[1]!.length === 4) {
      continue; // Skip ISO / year-first / non-numeric values.
    }
    const first = Number(match[1]);
    const second = Number(match[2]);
    if (first > 12 && second <= 12) {
      sawDMY = true;
    } else if (second > 12 && first <= 12) {
      sawMDY = true;
    }
  }

  if (sawDMY && !sawMDY) {
    return 'DMY';
  }
  if (sawMDY && !sawDMY) {
    return 'MDY';
  }
  return preferred;
}

/**
 * Parses a statement date value to a `yyyy-MM-dd` string.
 *
 * Mirrors the previous behaviour of returning today's date for empty values and
 * the original trimmed string when parsing fails, but understands both mm/dd and
 * dd/mm ordering via the supplied {@link DateOrder} (EZ-4027).
 */
export function parseStatementDate(dateValue: unknown, order: DateOrder = 'MDY'): string {
  if (typeof dateValue === 'string' && dateValue.trim()) {
    const trimmed = dateValue.trim();
    const iso = parseToISO(trimmed, order);
    return iso ?? trimmed;
  }

  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Returns true when a value can be parsed as a valid statement date for the
 * given ordering.
 */
export function isValidStatementDate(
  value: string | number | undefined,
  order: DateOrder = 'MDY',
): boolean {
  if (!value || typeof value === 'number') {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return parseToISO(trimmed, order) !== null;
}
